import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { MatDialog } from '@angular/material';

import * as FileSaver from 'file-saver';

import {
  MatchService,
  ApplicationEventService,
  PlaylistService,
  PublishedMatchService,
  FolderService
} from '../../../services';
import {
  MatchEvent,
  CodeEventGroup,
  Playlist,
  ExportVideoModel,
  ExportEvent,
  Match,
  Folder,
  ClipData} from '../../../models';
import { EditGameDialogComponent, AddPlaylistDialogComponent, AddToPlaylistDialogComponent, EditPlaylistDialogComponent } from '../../dialogs';
import { Subject } from 'rxjs/Subject';
import { AngularFireAuth } from '@angular/fire/auth';
import * as common from '../../../common';
import { isArray } from 'util';
import { ClipViewerDialogComponent } from '../../../common-components/dialogs';

@Component({
  selector: 'match-editor',
  templateUrl: './match-editor.component.html',
  styleUrls: ['./match-editor.component.css']
})
export class MatchEditorComponent implements OnInit {
  userId: string;
  currentMatch: Match;
  matchEvents: MatchEvent[];
  opened = true;
  playState$ = new Subject<boolean>();
  playlists: Playlist[];
  folders: Folder[];

  constructor(
    private authService: AngularFireAuth,
    private activatedRoute: ActivatedRoute,
    private matchService: MatchService,
    private publishedMatchService: PublishedMatchService,
    private applicationEventService: ApplicationEventService,
    private playlistService: PlaylistService,
    private folderService: FolderService,
    public dialog: MatDialog  ) {}

  ngOnInit() {
    const matchKey = this.activatedRoute.snapshot.params['id'];
    this.getMatch(matchKey);

    this.authService.authState.subscribe(user => {
      if (user) {
        this.userId = user.uid;
        this.playlistService.getForMatch(matchKey).subscribe(playlists => {
          this.playlists = playlists;
        });

        this.folderService.getForOwnerUser(user.uid).subscribe(folders => {
          this.folders = folders;
        });
      }
    });
  }

  getMatch(matchKey: string) {
    console.info(`[MatchEditor] Retrieving match: ${matchKey}`);
    this.matchService.get(matchKey).subscribe(match => {
      if (match.isPublished === undefined) {
        match.isPublished = false;
      }

      if (match.folders === undefined) {
        match.folders = [];
      }

      console.info(`[MatchEditor] Match loaded`, match);
      this.currentMatch = match;
    });
  }

  onFolderChange(folder: Folder, checked: boolean) {
    console.log(`Checkbox checked: ${checked}`, folder);

    if (folder.matches === undefined || !isArray(folder.matches)) {
      folder.matches = [];
    }

    if (checked) {
      const newFolders = [...this.currentMatch.folders, { id: folder.id, name: folder.name }];
      this.currentMatch.folders = newFolders;
      this.matchService.update(this.currentMatch);

      const newMatches = [
        ...folder.matches,
        { id: this.currentMatch.id, name: this.currentMatch.name }
      ];
      folder.matches = newMatches;
      this.folderService.update(folder);
    } else {
      const newFolders = this.currentMatch.folders.filter(e => e.id !== folder.id);
      this.currentMatch.folders = newFolders;
      this.matchService.update(this.currentMatch);

      const newMatches = folder.matches.filter(e => e.id !== this.currentMatch.id);
      folder.matches = newMatches;
      this.folderService.update(folder);
    }
  }

  isCurrentFolder(folder: Folder): boolean {

    if (!this.currentMatch.folders) {
      return false;
    }

    const result = this.currentMatch.folders.find(view => view.id === folder.id);
    return result !== undefined;
  }

  onEditMatch(match: Match): void {
    const dialogRef = this.dialog.open(EditGameDialogComponent, {
      data: match
    });

    dialogRef.afterClosed().subscribe((m: Match) => {
      if (m !== undefined) {
        this.currentMatch = m;
      }
    });
  }

  onAddCodeEvent(codeEvent: MatchEvent) {
    console.info('MatchEditorComponent:onCodeEvent', this.currentMatch, codeEvent);
    const item = this.currentMatch.events
      ? this.currentMatch.events.find(e => e.eventType === codeEvent.eventType)
      : undefined;

    if (item) {
      this.currentMatch.isCurrentPublished = false;

      item.events.push(codeEvent);
      console.info('[MatchEditor] onCodeEvent (1)', this.currentMatch);
      this.matchService.update(this.currentMatch);
    } else {
      const codeEventGroup: CodeEventGroup = {
        eventType: codeEvent.eventType,
        events: [codeEvent]
      };

      this.currentMatch.events.push(codeEventGroup);
      console.info('[MatchEditor] onCodeEvent (2)', this.currentMatch);
      this.matchService.update(this.currentMatch);
    }
  }

  onDeleteCodeEvent(codeEvent: MatchEvent) {
    console.info('MatchEditorComponent:onDeleteCodeEvent', this.currentMatch);

    const response = confirm('Are you sure you want to delete the code event?');
    if (response) {

      console.info(`MatchEditorComponent:onDeleteCodeEvent - Scanning for ${codeEvent.eventType}`, codeEvent, this.currentMatch.events);

      const item = this.currentMatch.events
        ? this.currentMatch.events.find(e => e.eventType === codeEvent.eventType)
        : undefined;

      if (item) {

        console.info(`MatchEditorComponent:onDeleteCodeEvent - found item`);

        this.currentMatch.isCurrentPublished = false;

        const events = item.events.filter(e => e.seconds !== codeEvent.seconds);
        if (events.length > 0) {
          item.events = events;
        } else {
          this.currentMatch.events = this.currentMatch.events.filter(
            g => g.eventType !== item.eventType
          );
        }

        this.matchService.update(this.currentMatch);
      }
    }
  }

  onPublishChanged(isPublished: boolean) {
    this.currentMatch.isPublished = isPublished;
    this.currentMatch.isCurrentPublished = isPublished;

    if (isPublished) {  // this means the match was previously unpublished
      const publishedMatch = MatchService.createPublishedMatch(this.currentMatch);

      console.info('[MatchEditor] Publishing match: ', this.currentMatch, publishedMatch);

      this.publishedMatchService.add(publishedMatch);

      this.matchService.update(this.currentMatch);
    } else { // we need to unpublish it

      console.info('[MatchEditor] Unpublishing match: ', this.currentMatch.id);
      this.publishedMatchService.removePublishedMatch(this.currentMatch.id);

      this.matchService.update(this.currentMatch);
    }
  }

  onRepublish() {
    this.publishedMatchService.getByMatchId(this.currentMatch.id).subscribe(p => {
      // Get current state
      const publishedMatch = MatchService.createPublishedMatch(this.currentMatch);
      console.info('[MatchEditor] Publishing match: ', this.currentMatch, publishedMatch);

      // TODO: Update other properties here or just events?
      p.events = publishedMatch.events;
      this.publishedMatchService.update(p);

      // Ensure Match is published
      this.currentMatch.isPublished = true;
      this.currentMatch.isCurrentPublished = true;
      this.matchService.update(this.currentMatch);
    });
  }

  onShowPlaylistRow(group: { eventType: string; events: MatchEvent[] }) {

    const match = this.currentMatch;
    const clips = group.events.map(e => {
      const clip = { ...e };
      return clip;
    });

    const clipData = { match, clips, name: group.eventType } as ClipData;

    this.playState$.next(false);

    const dialogRef = this.dialog.open(ClipViewerDialogComponent, {
      height: '580px',
      width: '1010px',
      data: clipData
    });

  }

  onAddPlaylist() {
    const dialogRef = this.dialog.open(AddPlaylistDialogComponent, {
      height: '250px',
      width: '300px',
      data: this.currentMatch.id
    });

    dialogRef.afterClosed().subscribe((playlist: Playlist) => {
      if (playlist !== undefined) {
        console.info(`Added Playlist ${playlist.id}`);
      }
    });
  }

  onAddToPlaylist(codeEvents: MatchEvent[]) {
    console.info('[MatchEditorComponent] onAddToPlaylist', codeEvents);

    const playlists = [...this.playlists];

    this.dialog.open(AddToPlaylistDialogComponent, {
      height: '250px',
      width: '300px',
      data: { codeEvents, playlists }
    });
  }

  onEditPlaylist(playlist: Playlist) {
    console.info('[MatchEditorComponent] onEditPlaylist', playlist);

    this.dialog.open(EditPlaylistDialogComponent, {
      height: '500px',
      width: '450px',
      data: playlist
    });
  }

  onDeletePlaylist(playlist: Playlist) {
    const response = confirm('Are you sure you want to delete the playlist?');
    if (response) {
      this.playlistService.delete(playlist.id);
    }
  }

  onViewCodeClip(matchEvent: MatchEvent) {
    const match = this.currentMatch;
    const clipData = { match, clips: [matchEvent], name: matchEvent.eventType } as ClipData;

    this.playState$.next(false);

    this.dialog.open(ClipViewerDialogComponent, {
        height: '600px',
        width: '1010px',
        data: clipData
    });

  }

  onShowPlaylist(playlist: Playlist) {
    console.info('Showing playlist: ', playlist);
    const match = this.currentMatch;
    const clips = playlist.items.map(e => {
    const clip = { ...e, eventType: e.name };
      return clip;
    });

    const clipData = { match, clips, name: playlist.name, playlistId: playlist.id } as ClipData;

    this.playState$.next(false);

    this.dialog.open(ClipViewerDialogComponent, {
      height: '580px',
      width: '1010px',
      data: clipData
    });

  }

  onExportPlaylistVideo(playlist: Playlist) {
    console.info('Creating playlist video: ', playlist);
    const playlistName = playlist.name;
    const videoUrl = this.currentMatch.media.src;

    const clips: ExportEvent[] = playlist.items.map(e => {
      const start = common.secondsToTime(e.seconds);
      const duration = (e.leadSeconds + e.lagSeconds).toString();

      const clip = { start, duration } as ExportEvent;
      if (e.comments) {
        clip.description = e.comments;
      }
      return clip;
    });

    console.info('[onCreatePlaylistVideo] transformed: ', playlist.items, clips);

    const data = { playlistName, clips, videoUrl } as ExportVideoModel;

    this.exportFile(data);

    // this.playState$.next(false);

    // let dialogRef = this.dialog.open(ExportVideoDialog, {
    //   height: "580px",
    //   width: "1010px",
    //   data: data
    // });
  }

  onExportRowVideo(group: { eventType: string; events: MatchEvent[] }) {
    console.info('Creating row video: ', group);
    const playlistName = group.eventType;
    const videoUrl = this.currentMatch.media.src;

    const clips: ExportEvent[] = group.events.map(e => {
      const start = common.secondsToTime(e.seconds);
      const duration = (e.leadSeconds + e.lagSeconds).toString();

      const clip = { start, duration } as ExportEvent;
      if (e.comments) {
        clip.description = e.comments;
      }
      return clip;
    });

    console.info('[onCreateRowVideo] transformed: ', group.events, clips);

    const data = { playlistName, clips, videoUrl } as ExportVideoModel;

    this.exportFile(data);
  }

  exportFile(data: ExportVideoModel) {
    const txt = JSON.stringify(data);
    const blob = new Blob([txt], { type: 'text/plain' });

    FileSaver.saveAs(blob, `${data.playlistName}.json`);
  }

  onGameChanged() {
    this.applicationEventService.matchChanged(this.currentMatch);
  }
}
